import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import { useUserStore } from "@/stores/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const databaseService = serviceHost.getDatabaseService();

const productRepository = repositoryHost.getProductRepository(databaseService);

type CreateProductParams = Parameters<typeof productRepository.create>[0];
type UpdateProductParams = Parameters<typeof productRepository.update>[0];
type DeleteProductParams = Parameters<typeof productRepository.delete>[0];

export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  const { hasFullAccess } = useUserStore();

  return useMutation<string, Error, CreateProductParams>({
    mutationKey: ["product", "create"],
    mutationFn: async (params: CreateProductParams) => {
      if (!hasFullAccess()) {
        throw new Error(
          "Access denied: Full access required to create products",
        );
      }
      return productRepository.create(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products", "get"] });
    },
  });
};

export const useGetProducts = () => {
  return useQuery({
    queryKey: ["products", "get"],
    queryFn: async () =>
      await productRepository.getAll({
        orderBy: {
          field: "name",
          direction: "asc",
        },
      }),
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  const { hasFullAccess } = useUserStore();

  return useMutation<void, Error, UpdateProductParams>({
    mutationKey: ["products", "update"],
    mutationFn: async (params: UpdateProductParams) => {
      if (!hasFullAccess()) {
        throw new Error(
          "Access denied: Full access required to update products",
        );
      }
      return productRepository.update(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products", "get"] });
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  const { hasFullAccess } = useUserStore();

  return useMutation<void, Error, DeleteProductParams>({
    mutationKey: ["product", "delete"],
    mutationFn: async (params: DeleteProductParams) => {
      if (!hasFullAccess()) {
        throw new Error(
          "Access denied: Full access required to delete products",
        );
      }
      return productRepository.delete(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products", "get"] });
    },
  });
};
